import { useEffect } from 'react';

import { expValEquals } from '@confluence/feature-experiments';
import { useFullPageEditorApiState } from '@confluence/full-page-editor-store';
import {
	createSingleQueryParamHook,
	useRouteActions,
} from '@confluence/route-manager/entry-points/RouteState.ts';

import {
	SUGGESTIONS_PANEL_OPEN_QUERY_VALUE,
	SUGGESTIONS_PANEL_QUERY_PARAM,
} from './suggestionsPanelDeepLink.ts';
import { useShowSuggestionsPanel } from './useShowSuggestionsPanel.ts';

const useSuggestionsPanelQueryParam = createSingleQueryParamHook(SUGGESTIONS_PANEL_QUERY_PARAM);

/**
 * Shows the Suggestions panel with the page's persisted suggestions when the editor is loaded with
 * `suggestionsPanel=open`, then removes the parameter so a reload does not show it again. Removing
 * the parameter is what prevents a repeat; there is no per-mount flag because live doc to live doc
 * navigation keeps this hook mounted, and the next doc's deep link must still work.
 *
 * The hook waits until collaborative editing has loaded the document, then refreshes the persisted
 * suggestions before showing the panel. The AI Suggestions plugin also loads them when its editor
 * view is created, but on an in-app navigation (for example from an Agent Session card on Home)
 * that load can run against a document that has not loaded yet and drop them as stale, leaving the
 * panel empty. Collab readiness covers both regular pages and live docs.
 */
export const useShowSuggestionsPanelFromDeepLink = (): void => {
	const suggestionsPanelQueryParam = useSuggestionsPanelQueryParam();
	const { setQueryParams } = useRouteActions();
	const { showSuggestionsPanel } = useShowSuggestionsPanel();
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();

	useEffect(() => {
		// The experiment is checked last so ordinary editor loads are not exposed to it.
		if (
			suggestionsPanelQueryParam !== SUGGESTIONS_PANEL_OPEN_QUERY_VALUE ||
			!fullPageEditorPresetAPI?.aiSuggestions ||
			!expValEquals('cc_ai_agentic_sessions', 'isEnabled', true)
		) {
			return;
		}

		const { aiSuggestions, collabEdit } = fullPageEditorPresetAPI;
		let hasShown = false;
		let unsubscribe: (() => void) | undefined;

		const showWithPersistedSuggestions = () => {
			if (hasShown) {
				return;
			}
			hasShown = true;
			unsubscribe?.();
			aiSuggestions.actions.refreshSuggestions();
			showSuggestionsPanel({ entryPoint: 'suggestionsPanel' });
			setQueryParams({ [SUGGESTIONS_PANEL_QUERY_PARAM]: null }, true);
		};

		if (!collabEdit || collabEdit.sharedState.currentState()?.initialised.collabInitialisedAt) {
			showWithPersistedSuggestions();
			return;
		}

		unsubscribe = collabEdit.sharedState.onChange(({ nextSharedState }) => {
			if (nextSharedState?.initialised.collabInitialisedAt) {
				showWithPersistedSuggestions();
			}
		});

		return () => {
			hasShown = true;
			unsubscribe?.();
		};
	}, [fullPageEditorPresetAPI, setQueryParams, showSuggestionsPanel, suggestionsPanelQueryParam]);
};
